import * as radio from "@zag-js/radio-group";
import { useMachine, normalizeProps } from "@zag-js/react";
import { FC, HTMLAttributes, ReactNode, useId, useState } from "react";
import { Button } from "../button";
import { EmptyText } from "../empty-text";

export interface RadioGroupItem {
  id: string;
  label: ReactNode;
  description?: ReactNode;
}

export interface RadioGroupProps {
  renderLabel: (labelProps: HTMLAttributes<HTMLElement>) => ReactNode;
  items: RadioGroupItem[];
  additionalItems?: RadioGroupItem[];
  onChange: (details: { value: string }) => void;
  value: string | undefined;
}

export const RadioGroup: FC<RadioGroupProps> = ({
  renderLabel,
  items: originalItems,
  additionalItems = [],
  onChange,
  value,
}) => {
  const [showMore, setShowMore] = useState(false);
  const items = [...originalItems, ...(showMore ? additionalItems : [])];

  const id = useId();
  const [state, send] = useMachine(radio.machine({ id, value: items[0]?.id }), {
    context: {
      onChange: onChange,
      value,
    },
  });
  const api = radio.connect(state, send, normalizeProps);

  const displayShowMoreButton = additionalItems.length !== 0 && !showMore;

  return (
    <div {...api.rootProps} className="flex flex-col gap-2.5">
      {renderLabel(api.labelProps)}
      <div>
        {items.map(({ id, label, description }) => {
          const isSelected = id === api.value;

          return (
            <label
              key={id}
              {...api.getRadioProps({ value: id })}
              className="flex items-center gap-2.5 cursor-pointer py-2.5 justify-between"
            >
              <div className="flex items-center gap-2.5">
                <div
                  {...api.getRadioControlProps({ value: id })}
                  className={`flex items-center justify-center w-5 h-5 border-2 rounded-full transition-colors duration-200 ${
                    isSelected ? "border-white" : "border-blue-300"
                  }`}
                >
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${
                      isSelected ? "bg-white scale-100" : "bg-blue-300 scale-0"
                    } transition-all duration-200`}
                  />
                </div>
                <span
                  {...api.getRadioLabelProps({ value: id })}
                  className={`font-medium transition-colors duration-200 ${
                    isSelected ? "text-white" : "text-blue-300"
                  }`}
                >
                  {label}
                </span>
                <input {...api.getRadioInputProps({ value: id })} />
              </div>
              {description && (
                <div className="text-shale-200">{description}</div>
              )}
            </label>
          );
        })}
      </div>
      {displayShowMoreButton && (
        <div className="flex">
          <Button variant="link" onClick={() => setShowMore(true)}>
            Show more
          </Button>
        </div>
      )}
    </div>
  );
};

export interface PulseRadioGroupProps {
  label: ReactNode;
  countOfItems: number;
}

export const PulseRadioGroup: FC<PulseRadioGroupProps> = ({
  label,
  countOfItems,
}) => {
  return (
    <div className="flex flex-col gap-2.5 animate-pulse">
      {label}
      <div>
        {[...Array(countOfItems)].map((_, i) => {
          return (
            <div
              // static content, so index can be used
              key={i}
              className="flex items-center gap-2.5 py-2.5"
            >
              <div className="w-5 h-5 rounded-full bg-shale-700" />
              <div className="w-32 rounded-lg bg-shale-700">
                <EmptyText />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
